import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Award,
  Loader2,
  Save,
  Download,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  AlertTriangle,
  FileText,
  StickyNote,
  CreditCard,
  Sparkles,
  RefreshCw,
} from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { cn, formatCurrency } from '@/lib/utils'
import { resizeImageToDataUrl } from '@/lib/imageUpload'
import type {
  APIResponse,
  GoogleClassSyncStatus,
  LoyaltyConfig,
  PassHealth,
  PublicLinksConfig,
  WalletStats,
} from '@/types'
import { MarketingShell } from './MarketingShell'
import { LoyaltyScreenPreview } from './LoyaltyScreenPreview'
import { LoyaltyEligibleItemsDialog } from './LoyaltyEligibleItemsDialog'
import { TokenChips, insertAtCursor } from './copyTokens'

// Defaults the receipt renderer falls back to when the admin hasn't
// supplied custom copy. Kept here (not in the renderer) so the admin
// UI and the print path agree on what "default" looks like — change
// them together.
const DEFAULT_RECEIPT_HEADLINE = 'JOIN OUR LOYALTY PROGRAM'
const DEFAULT_RECEIPT_PROGRESS = '{{remaining}} more visit{{s}} to {{reward}}'

// Sample "Awarded to …" line for the receipt preview. Mirrors the
// loyalty-saved-to line the print path renders (printCustomerReceipt.ts)
// when a guest was captured at the counter: first name + last-4 of phone,
// masked. The actual receipt substitutes the real guest's details; this
// is purely illustrative. · = the same middle dot the slip prints,
// not an ASCII period — keep them in sync.
const PREVIEW_SAVED_TO = 'Awarded to Maryam · ····1234'

interface DraftState {
  isEnabled: boolean
  thresholdVisits: string
  rewardLabel: string
  rewardDiscountType: 'flat' | 'percent'
  rewardDiscountValue: string
  // Earn-side gates. earnMinSubtotal: empty string = no floor. The cooldown is
  // modelled as an on/off flag plus a free hours value (persisted as minutes);
  // we keep the hours around while it's off so re-enabling restores it.
  earnMinSubtotal: string
  earnCooldownEnabled: boolean
  earnCooldownHours: string
  // What a stamp counts against: the bill ('order', default) or each qualifying
  // unit sold ('item'). earnMaxPerOrder caps an item-mode bill; empty = no cap.
  earnBasis: 'order' | 'item'
  earnMaxPerOrder: string
  // Empty string = "use default copy". We preserve that distinction
  // (vs. always seeding the textarea with the default text) so we
  // don't accidentally persist the default into the DB on first save.
  receiptHeadline: string
  receiptProgress: string
  // Card appearance — overrides for the scanned screen + wallet pass. Empty
  // string = inherit the receipt branding (or auto copy, for headline/subtext).
  screenAccentColor: string
  // Page background of the public loyalty pages. Empty = the default warm
  // cream. Editable from Marketing → Enrolment too; carried through here so
  // saving this form can't wipe a background set there.
  screenBackgroundColor: string
  screenLogoUrl: string
  // Logo for the public web pages (join + card screens) only. Empty =
  // inherit screenLogoUrl, i.e. the wallet-pass logo, which is what every
  // tenant saw before the two were separable.
  screenPageLogoUrl: string
  screenBusinessName: string
  screenHeadline: string
  screenSubtext: string
  // Staff phones may scan wallet cards and attach guests to open orders
  // via the /scan surface. Off by default; a per-venue owner decision.
  staffPhoneScanEnabled: boolean
}

function configToDraft(cfg: LoyaltyConfig): DraftState {
  return {
    isEnabled: cfg.is_enabled,
    thresholdVisits: String(cfg.threshold_visits),
    rewardLabel: cfg.reward_label,
    rewardDiscountType: cfg.reward_discount_type,
    rewardDiscountValue: String(cfg.reward_discount_value),
    earnMinSubtotal: cfg.earn_min_subtotal != null ? String(cfg.earn_min_subtotal) : '',
    earnCooldownEnabled: cfg.earn_cooldown_minutes != null && cfg.earn_cooldown_minutes > 0,
    // UI works in hours; persisted value is minutes. Pre-fill 6h when nothing
    // is set yet so flipping the toggle on isn't a blank field.
    earnCooldownHours:
      cfg.earn_cooldown_minutes != null && cfg.earn_cooldown_minutes > 0
        ? String(cfg.earn_cooldown_minutes / 60)
        : '6',
    earnBasis: cfg.earn_basis === 'item' ? 'item' : 'order',
    earnMaxPerOrder: cfg.earn_max_per_order != null ? String(cfg.earn_max_per_order) : '',
    receiptHeadline: cfg.receipt_headline ?? '',
    receiptProgress: cfg.receipt_progress_message ?? '',
    screenAccentColor: cfg.screen_accent_color ?? '',
    screenBackgroundColor: cfg.screen_background_color ?? '',
    screenLogoUrl: cfg.screen_logo_url ?? '',
    screenPageLogoUrl: cfg.screen_page_logo_url ?? '',
    screenBusinessName: cfg.screen_business_name ?? '',
    screenHeadline: cfg.screen_headline ?? '',
    screenSubtext: cfg.screen_subtext ?? '',
    staffPhoneScanEnabled: cfg.staff_phone_scan_enabled ?? false,
  }
}

// Mirrors substituteLoyaltyCopy in printCustomerReceipt.ts — kept in
// lockstep so the preview matches what actually prints. Supported
// placeholders: {{remaining}}, {{punches}}, {{threshold}}, {{reward}},
// and {{s}} (auto-pluraliser, empty when remaining === 1).
function substituteLoyaltyCopy(
  tpl: string,
  ctx: { remaining: number; punches: number; threshold: number; reward: string },
): string {
  return tpl
    .replace(/\{\{remaining\}\}/g, String(ctx.remaining))
    .replace(/\{\{punches\}\}/g, String(ctx.punches))
    .replace(/\{\{threshold\}\}/g, String(ctx.threshold))
    .replace(/\{\{reward\}\}/g, ctx.reward)
    .replace(/\{\{s\}\}/g, ctx.remaining === 1 ? '' : 's')
}

// Normalise a hex colour to the 6-digit form an <input type="color"> and the
// preview require, expanding #abc → #aabbcc. Returns '' for anything that
// isn't a 3/6-digit hex so callers can fall through to the next source.
function toSwatchHex(hex: string): string {
  const v = hex.trim()
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase()
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    return ('#' + v.slice(1).split('').map((c) => c + c).join('')).toLowerCase()
  }
  return ''
}

/**
 * Single-rule visit-based loyalty settings. The admin picks the
 * threshold + reward; the counter does the math. Stats below show
 * recent activity and the customers with the largest current balance.
 */
export function AdminLoyaltySettings() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const configQuery = useQuery<APIResponse<LoyaltyConfig>>({
    queryKey: ['loyalty-config'],
    queryFn: () => apiClient.getLoyaltyConfig(),
    staleTime: 60_000,
  })
  // Pulled so we can warn the admin when their public URL isn't
  // scannable BEFORE they print posters. Shared query key with the
  // Settings page so flipping the URL there refreshes this banner.
  const linksQuery = useQuery<APIResponse<PublicLinksConfig>>({
    queryKey: ['public-links'],
    queryFn: () => apiClient.getPublicLinks(),
    staleTime: 60_000,
  })
  // Receipt branding — the fallback the loyalty card-appearance overrides
  // inherit when left blank. Shared query key with the Settings page so
  // editing the receipt logo/accent there refreshes the placeholders +
  // preview here.
  const settingsQuery = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 60_000,
  })

  // Which items earn stamps. Lives outside the config blob (it's a join table),
  // so it's its own query. An EMPTY array means "all items earn" — the default —
  // which is why the count drives the copy in several places below.
  const eligibleQuery = useQuery({
    queryKey: ['loyalty-eligible-products'],
    queryFn: () => apiClient.getLoyaltyEligibleProducts().then((r) => r.data ?? []),
    staleTime: 60_000,
  })
  const eligibleCount = eligibleQuery.data?.length ?? 0

  const [draft, setDraft] = useState<DraftState | null>(null)
  const [eligibleOpen, setEligibleOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [appearanceOpen, setAppearanceOpen] = useState(false)
  const screenLogoInputRef = useRef<HTMLInputElement | null>(null)
  const pageLogoInputRef = useRef<HTMLInputElement | null>(null)
  // The card banner lives in its own app_settings key rather than inside
  // loyalty.config: that blob is re-read on every sale by the earn path, and
  // an inlined base64 image has no business on the hot payment path. So it
  // gets its own draft state and its own save call.
  const heroInputRef = useRef<HTMLInputElement | null>(null)
  const [heroDraft, setHeroDraft] = useState<string | null>(null)
  const [posterMenuOpen, setPosterMenuOpen] = useState(false)
  const [posterPendingLayout, setPosterPendingLayout] = useState<string | null>(null)
  // Guard before turning an active programme off — a destructive-ish action
  // for any customer mid-way to a reward.
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false)
  const progressRef = useRef<HTMLTextAreaElement>(null)

  async function openPoster(layout: 'a4' | 'tent' | 'card') {
    setPosterPendingLayout(layout)
    try {
      const html = await apiClient.fetchLoyaltyPosterHTML(layout)
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const w = window.open(url, '_blank', 'noopener')
      if (!w) {
        toast({
          title: 'Pop-up blocked',
          description: 'Allow pop-ups for this site and try again.',
          variant: 'destructive',
        })
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      setPosterMenuOpen(false)
    } catch (err) {
      toast({
        title: 'Could not open poster',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      })
    } finally {
      setPosterPendingLayout(null)
    }
  }

  useEffect(() => {
    if (configQuery.data?.data) {
      const next = configToDraft(configQuery.data.data)
      setDraft(next)
      // Auto-expand the rule body when the programme is on, so the
      // admin sees the live config without an extra click. Matches the
      // Comeback coupon card behaviour on the Promo codes page.
      if (next.isEnabled) setExpanded(true)
    }
  }, [configQuery.data])

  const saveMutation = useMutation({
    mutationFn: async (data: LoyaltyConfig) => {
      // Banner first: it feeds the branding fingerprint the backend hashes
      // when it decides whether to push the Google class, so writing it
      // before the config means one save produces one push carrying both.
      if (heroDirty) await apiClient.updateSetting('loyalty_hero_url', heroUrl)
      return apiClient.updateLoyaltyConfig(data)
    },
    onSuccess: () => {
      toast({ title: 'Loyalty program saved' })
      setHeroDraft(null)
      queryClient.invalidateQueries({ queryKey: ['loyalty-config'] })
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
      // The save also kicks a Google branding push on the backend. Refetch
      // the wallet panel (after a beat, so the async push has landed) to
      // show whether it took.
      setTimeout(
        () => queryClient.invalidateQueries({ queryKey: ['admin-wallet-stats'] }),
        2_000,
      )
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Save failed', description: msg, variant: 'destructive' })
    },
  })

  // Dirty-state detection so the Save button feels responsive: flips
  // from "Save changes" → "Saved" (disabled) once the on-disk config
  // matches the live draft. Mirrors AdminComebackCouponSettings.
  // Computed BEFORE the loading early-return so hook order stays
  // stable between renders (React: "Rendered more hooks than during
  // the previous render" otherwise).
  const persistedDraft = useMemo(
    () => (configQuery.data?.data ? configToDraft(configQuery.data.data) : null),
    [configQuery.data],
  )
  const configDirty = useMemo(
    () => (persistedDraft && draft ? JSON.stringify(persistedDraft) !== JSON.stringify(draft) : false),
    [persistedDraft, draft],
  )

  // Receipt branding the card-appearance overrides fall back to. getAllSettings
  // returns already-decoded values, so these are read as plain strings.
  const receiptBranding = useMemo(() => {
    const d = settingsQuery.data?.data as Record<string, unknown> | undefined
    return {
      businessName: typeof d?.receipt_business_name === 'string' ? d.receipt_business_name : '',
      logoUrl: typeof d?.receipt_logo_url === 'string' ? d.receipt_logo_url : '',
      accentColor: typeof d?.receipt_accent_color === 'string' ? d.receipt_accent_color : '',
    }
  }, [settingsQuery.data])

  // Persisted banner, and the effective value the preview + save use.
  const persistedHero = useMemo(() => {
    const d = settingsQuery.data?.data as Record<string, unknown> | undefined
    return typeof d?.loyalty_hero_url === 'string' ? d.loyalty_hero_url : ''
  }, [settingsQuery.data])
  const heroUrl = heroDraft ?? persistedHero
  const heroDirty = heroDraft !== null && heroDraft !== persistedHero
  // The banner saves alongside the config, so it has to arm the same button.
  const dirty = configDirty || heroDirty

  if (!draft) {
    return (
      <MarketingShell title="Loyalty program">
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </MarketingShell>
    )
  }

  function handleSave() {
    if (!draft) return
    const threshold = Number(draft.thresholdVisits)
    const value = Number(draft.rewardDiscountValue)
    if (!Number.isFinite(threshold) || threshold < 1 || threshold > 100) {
      toast({ title: 'Invalid threshold', description: 'Threshold must be 1-100 visits.', variant: 'destructive' })
      return
    }
    if (!Number.isFinite(value) || value <= 0) {
      toast({ title: 'Invalid reward value', description: 'Reward value must be greater than 0.', variant: 'destructive' })
      return
    }
    if (draft.rewardDiscountType === 'percent' && value > 100) {
      toast({ title: 'Percent too high', description: 'Percent reward caps at 100.', variant: 'destructive' })
      return
    }
    const earnMin = draft.earnMinSubtotal.trim() === '' ? null : Number(draft.earnMinSubtotal)
    if (earnMin != null && (!Number.isFinite(earnMin) || earnMin < 0)) {
      toast({ title: 'Invalid minimum spend', description: 'Minimum spend to earn must be 0 or more.', variant: 'destructive' })
      return
    }
    let earnCooldownMinutes: number | null = null
    if (draft.earnCooldownEnabled) {
      const hours = Number(draft.earnCooldownHours)
      if (!Number.isFinite(hours) || hours <= 0) {
        toast({ title: 'Invalid cooldown', description: 'Enter how many hours between earned visits (greater than 0).', variant: 'destructive' })
        return
      }
      earnCooldownMinutes = Math.round(hours * 60)
      if (earnCooldownMinutes > 43200) {
        toast({ title: 'Cooldown too long', description: 'The cooldown caps at 30 days (720 hours).', variant: 'destructive' })
        return
      }
    }
    // The cap only exists in item mode; send null otherwise so a value left
    // behind from a previous mode can't quietly come back if they switch again.
    let earnMaxPerOrder: number | null = null
    if (draft.earnBasis === 'item' && draft.earnMaxPerOrder.trim() !== '') {
      const cap = Number(draft.earnMaxPerOrder)
      if (!Number.isFinite(cap) || cap < 1 || cap > 100) {
        toast({ title: 'Invalid stamp cap', description: 'The most stamps one bill can earn must be between 1 and 100. Leave it blank for no cap.', variant: 'destructive' })
        return
      }
      earnMaxPerOrder = Math.round(cap)
    }
    const screenAccent = draft.screenAccentColor.trim()
    if (screenAccent !== '' && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(screenAccent)) {
      toast({ title: 'Invalid accent colour', description: 'Use a hex colour like #d97706, or leave it blank to use your receipt accent.', variant: 'destructive' })
      return
    }
    const screenBackground = draft.screenBackgroundColor.trim()
    if (screenBackground !== '' && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(screenBackground)) {
      toast({ title: 'Invalid background colour', description: 'Use a hex colour like #faf7f2, or leave it blank for the default.', variant: 'destructive' })
      return
    }
    saveMutation.mutate({
      is_enabled: draft.isEnabled,
      rule_type: 'visits_to_reward',
      threshold_visits: threshold,
      reward_label: draft.rewardLabel.trim() || 'Reward',
      reward_discount_type: draft.rewardDiscountType,
      reward_discount_value: value,
      earn_min_subtotal: earnMin,
      // The cooldown is ignored server-side in item mode; still send whatever
      // the operator has configured so flipping back to per-bill restores it.
      earn_cooldown_minutes: earnCooldownMinutes,
      earn_basis: draft.earnBasis,
      earn_max_per_order: earnMaxPerOrder,
      receipt_headline: draft.receiptHeadline.trim(),
      receipt_progress_message: draft.receiptProgress.trim(),
      screen_accent_color: screenAccent,
      screen_background_color: screenBackground,
      screen_logo_url: draft.screenLogoUrl.trim(),
      screen_page_logo_url: draft.screenPageLogoUrl.trim(),
      screen_business_name: draft.screenBusinessName.trim(),
      screen_headline: draft.screenHeadline.trim(),
      screen_subtext: draft.screenSubtext.trim(),
      staff_phone_scan_enabled: draft.staffPhoneScanEnabled,
    })
  }

  async function handleScreenLogoFile(file: File) {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image file (PNG, JPG, SVG).', variant: 'destructive' })
      return
    }
    try {
      const dataUrl = await resizeImageToDataUrl(file, 400, 0.92)
      setDraft((d) => (d ? { ...d, screenLogoUrl: dataUrl } : d))
    } catch (e) {
      toast({ title: 'Upload failed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' })
    }
  }

  async function handlePageLogoFile(file: File) {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image file (PNG, JPG, SVG).', variant: 'destructive' })
      return
    }
    try {
      const dataUrl = await resizeImageToDataUrl(file, 400, 0.92)
      setDraft((d) => (d ? { ...d, screenPageLogoUrl: dataUrl } : d))
    } catch (e) {
      toast({ title: 'Upload failed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' })
    }
  }

  async function handleHeroFile(file: File) {
    // SVG and WebP are deliberately excluded: the Go pass renderer decodes
    // PNG and JPEG only, so either format would preview correctly here and
    // then silently vanish from the actual wallet card.
    if (!/^image\/(png|jpeg)$/.test(file.type)) {
      toast({
        title: 'Unsupported format',
        description: 'Card banners must be PNG or JPG.',
        variant: 'destructive',
      })
      return
    }
    try {
      // 1400px covers the largest consumer with margin: Apple's strip is
      // 1125×432 at @3x (Google's heroImage is 1032×336). An earlier 1100px
      // was under the @3x width, so the sharpest variant was being upscaled.
      // When a CDN is configured the backend rehosts this and the size stops
      // mattering; the cap below is the no-CDN fallback.
      const dataUrl = await resizeImageToDataUrl(file, 1400, 0.85)
      if (dataUrl.length > 1_200_000) {
        toast({
          title: 'Image too large',
          description: 'Even after resizing this banner is over 1.2 MB. Try a simpler or less detailed image.',
          variant: 'destructive',
        })
        return
      }
      setHeroDraft(dataUrl)
    } catch (e) {
      toast({ title: 'Upload failed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' })
    }
  }

  const resolved = linksQuery.data?.data?.resolved
  const showUrlWarning = resolved ? !resolved.is_scannable && resolved.source !== 'env' : false

  // Receipt preview substitutions — sample a mid-way state (3 of 5
  // punches by default, scaling with whatever threshold the admin has
  // typed). Empty headline/progress fall back to the defaults so the
  // preview is never blank. Not memoised — cheap derivations and the
  // form state changes on every keystroke anyway.
  const previewThreshold = Math.max(1, Number(draft.thresholdVisits) || 5)
  const previewPunches = Math.max(0, Math.min(previewThreshold - 1, previewThreshold - 2))
  const previewRemaining = previewThreshold - previewPunches
  const previewRewardLabel = draft.rewardLabel.trim() || 'Free coffee'
  const previewHeadline = (draft.receiptHeadline.trim() || DEFAULT_RECEIPT_HEADLINE).toUpperCase()
  const previewProgress = substituteLoyaltyCopy(
    draft.receiptProgress.trim() || DEFAULT_RECEIPT_PROGRESS,
    {
      remaining: previewRemaining,
      punches: previewPunches,
      threshold: previewThreshold,
      reward: previewRewardLabel,
    },
  )

  // Effective (merged) card-appearance values for the scan-screen preview:
  // the loyalty override when set, else the receipt branding, else a default.
  const effAccent =
    toSwatchHex(draft.screenAccentColor) || toSwatchHex(receiptBranding.accentColor) || '#d97706'
  const effLogo = draft.screenLogoUrl.trim() || receiptBranding.logoUrl
  // Web-page logo: its own override → the wallet logo → the receipt logo.
  // Mirrors mergeLoyaltyBranding server-side.
  const effPageLogo = draft.screenPageLogoUrl.trim() || effLogo
  const effName = draft.screenBusinessName.trim() || receiptBranding.businessName || 'Loyalty rewards'
  const effHeadline = draft.screenHeadline.trim() || effName

  return (
    <MarketingShell
      title="Loyalty program"
      description="Visit-based punch card. Every guest's phone is the canonical key — no physical cards to carry."
    >
      <div className="space-y-6">
        {showUrlWarning && (
          <div className="flex items-start gap-2.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-100">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="font-semibold">Your public URL isn't configured</div>
              <div className="mt-0.5">
                Customers scanning your loyalty QR from outside your network won't be able to reach the claim page.
              </div>
            </div>
            <Link
              to="/admin/settings"
              search={{ section: 'public-links' } as never}
              className="shrink-0 rounded-md border border-amber-400 bg-amber-100 px-2.5 py-1 text-sm font-semibold text-amber-900 hover:bg-amber-200 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100"
            >
              Configure →
            </Link>
          </div>
        )}
        <div
          id="loyalty-rule"
          className="rounded-xl border border-border/80 bg-gradient-to-br from-primary/[0.03] via-background to-background shadow-sm"
        >
          {/* Header row — always visible. Single glance: what it is +
              whether it's on. Same visual vocabulary as the Comeback
              coupon card on the Promo codes page so the two marketing
              configs feel like a family. Bottom padding only when
              collapsed — when expanded the body provides its own. */}
          <div
            className={cn(
              'flex cursor-pointer select-none items-start gap-3 rounded-t-xl px-5 pt-5 transition-colors hover:bg-foreground/[0.015]',
              !expanded && 'rounded-b-xl pb-5',
            )}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            onClick={() => setExpanded((x) => !x)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setExpanded((x) => !x)
              }
            }}
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Award className="h-[18px] w-[18px]" aria-hidden />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-foreground">Rule</h3>
                {draft.isEnabled && (
                  <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-sm font-semibold uppercase tracking-wider text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                    On
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">
                One rule, applies to every customer. Tweak it once, the counter starts honouring it on the next paid order.
              </p>
            </div>
            <div
              className="flex shrink-0 items-center gap-3"
              onClick={(e) => e.stopPropagation()}
            >
              <Switch
                id="loyalty-enabled"
                checked={draft.isEnabled}
                onCheckedChange={(checked) => {
                  // Turning it OFF while it's on → confirm first. The switch
                  // stays visually on until the owner confirms (draft is
                  // unchanged), so cancelling is a true no-op.
                  if (!checked && draft.isEnabled) {
                    setDisableConfirmOpen(true)
                    return
                  }
                  setDraft({ ...draft, isEnabled: checked })
                  if (checked) setExpanded(true)
                }}
                aria-label="Enable loyalty programme"
              />
              <button
                type="button"
                onClick={() => setExpanded((x) => !x)}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={expanded ? 'Collapse' : 'Expand'}
              >
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {expanded && (
          <div className="space-y-5 px-5 pb-5 pt-5">
            <div className="h-px bg-border/60" />

          {/* Live preview — single sentence summarising the rule the
              admin just typed. Updates as the form changes so the
              owner can sanity-check the configuration before saving. */}
          <div className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Preview:</span>{' '}
            {(() => {
              const t = Number(draft.thresholdVisits) || 5
              const label = draft.rewardLabel.trim() || 'reward'
              const v = Number(draft.rewardDiscountValue) || 0
              const valueCopy =
                draft.rewardDiscountType === 'percent'
                  ? `${v}% off`
                  : `${formatCurrency(v)} off`
              return `After ${t} paid visits, customer gets ${valueCopy} as “${label}”.`
            })()}
          </div>

          {/* Staff phone scanning — per-venue opt-in for the /scan surface.
              Same draft→save→invalidate ride as every other config field. */}
          <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/20 px-3 py-2.5">
            <div className="min-w-0">
              <Label htmlFor="loyalty-staff-phone-scan" className="text-sm font-semibold">
                Staff phone scanning
              </Label>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Let staff open <span className="font-mono">/scan</span> on their own phone to scan a
                customer's wallet card and attach them to an open order. The visit is still added
                only when the order is paid.
              </p>
            </div>
            <Switch
              id="loyalty-staff-phone-scan"
              checked={draft.staffPhoneScanEnabled}
              onCheckedChange={(checked) => setDraft({ ...draft, staffPhoneScanEnabled: checked })}
              aria-label="Enable staff phone scanning"
            />
          </div>

          {/* Two-column body: rule + receipt-copy editors on the left,
              live receipt preview on the right. Same grid template as
              the Comeback coupon card so the two cards feel like a
              family. Collapses to a single column on narrow widths. */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-[1.05fr_1fr]">
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="loyalty-threshold" className="text-sm">Visits per reward</Label>
                  <Input
                    id="loyalty-threshold"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={100}
                    value={draft.thresholdVisits}
                    onChange={(e) => setDraft({ ...draft, thresholdVisits: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="loyalty-label" className="text-sm">Reward label</Label>
                  <Input
                    id="loyalty-label"
                    value={draft.rewardLabel}
                    onChange={(e) => setDraft({ ...draft, rewardLabel: e.target.value })}
                    placeholder="Free dessert"
                    maxLength={120}
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-sm">Reward type</Label>
                  <Select
                    value={draft.rewardDiscountType}
                    onValueChange={(v) =>
                      setDraft({ ...draft, rewardDiscountType: v as 'flat' | 'percent' })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat (PKR)</SelectItem>
                      <SelectItem value="percent">Percent (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="loyalty-value" className="text-sm">Reward value</Label>
                  <Input
                    id="loyalty-value"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={draft.rewardDiscountValue}
                    onChange={(e) => setDraft({ ...draft, rewardDiscountValue: e.target.value })}
                    placeholder={draft.rewardDiscountType === 'percent' ? '25' : '500'}
                  />
                </div>
              </div>

              {/* Receipt copy — headline (one short line, stars-flanked
                  on the slip) + progress message (shown to enrolled
                  customers mid-way). The "reward ready" celebration
                  and the "save this visit" QR copy stay auto-generated
                  — only the in-progress wording is up to the admin. */}
              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between">
                  <Label htmlFor="loyalty-receipt-headline" className="text-sm">Receipt headline</Label>
                  {draft.receiptHeadline.trim() !== '' &&
                    draft.receiptHeadline.trim() !== DEFAULT_RECEIPT_HEADLINE && (
                    <button
                      type="button"
                      onClick={() => setDraft({ ...draft, receiptHeadline: '' })}
                      className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    >
                      Reset to default
                    </button>
                  )}
                </div>
                <Input
                  id="loyalty-receipt-headline"
                  value={draft.receiptHeadline}
                  onChange={(e) =>
                    setDraft({ ...draft, receiptHeadline: e.target.value.toUpperCase() })
                  }
                  maxLength={32}
                  placeholder={DEFAULT_RECEIPT_HEADLINE}
                />
                <p className="text-sm text-muted-foreground">
                  Prints at the top of the loyalty block. Up to 32 characters so it fits one line.
                </p>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between">
                  <Label htmlFor="loyalty-receipt-progress" className="text-sm">Receipt copy</Label>
                  {draft.receiptProgress.trim() !== '' &&
                    draft.receiptProgress.trim() !== DEFAULT_RECEIPT_PROGRESS && (
                    <button
                      type="button"
                      onClick={() => setDraft({ ...draft, receiptProgress: '' })}
                      className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    >
                      Reset to default
                    </button>
                  )}
                </div>
                <Textarea
                  ref={progressRef}
                  id="loyalty-receipt-progress"
                  value={draft.receiptProgress}
                  onChange={(e) => setDraft({ ...draft, receiptProgress: e.target.value })}
                  rows={2}
                  placeholder={DEFAULT_RECEIPT_PROGRESS}
                />
                <TokenChips
                  tokens={[
                    { label: 'Visits left', token: '{{remaining}}' },
                    { label: 'Reward', token: '{{reward}}' },
                    { label: 'Punches', token: '{{punches}}' },
                    { label: 'Goal', token: '{{threshold}}' },
                    { label: '+s (plural)', token: '{{s}}' },
                  ]}
                  onInsert={(token) =>
                    insertAtCursor(progressRef.current, token, draft.receiptProgress, (v) =>
                      setDraft({ ...draft, receiptProgress: v }),
                    )
                  }
                />
              </div>

              {/* Advanced — collapsible bucket for the earn-side integrity
                  controls (minimum spend to earn a visit + the earn cooldown).
                  There is no redeem-side limit by design: a fairly earned
                  reward can be spent on any order. Most owners leave these at
                  their defaults (no min, no cooldown). */}
              <div className="rounded-md border border-border/70">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-muted-foreground hover:bg-muted/50"
                  aria-expanded={advancedOpen}
                >
                  {advancedOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  Advanced
                </button>
                {advancedOpen && (
                  <div className="space-y-4 border-t border-border/70 p-3">
                    {/* What a stamp is counted against. Per-bill is the default
                        and what almost everyone wants; per-item exists for shops
                        running a classic "buy 10 coffees, get 1 free" card. */}
                    <div className="space-y-2">
                      <Label className="text-sm">What earns a stamp</Label>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {(
                          [
                            { value: 'order', title: 'Each bill', body: 'One stamp per visit, however much they order.' },
                            { value: 'item', title: 'Each item', body: 'A stamp for every qualifying item on the bill.' },
                          ] as const
                        ).map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setDraft({ ...draft, earnBasis: opt.value })}
                            aria-pressed={draft.earnBasis === opt.value}
                            className={cn(
                              'rounded-md border px-3 py-2 text-left transition-colors',
                              draft.earnBasis === opt.value
                                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                : 'border-border/70 hover:bg-muted/50',
                            )}
                          >
                            <span className="block text-sm font-medium">{opt.title}</span>
                            <span className="block text-xs text-muted-foreground">{opt.body}</span>
                          </button>
                        ))}
                      </div>
                      {/* The arithmetic here surprises people: at a threshold of
                          5, one table ordering 5 coffees completes a card on a
                          single visit. Say it with their actual numbers rather
                          than leaving them to discover it in production. */}
                      {draft.earnBasis === 'item' && (
                        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
                          One bill with {draft.thresholdVisits || 'N'} qualifying items now completes a whole card
                          — a first-time guest could walk out with “{draft.rewardLabel.trim() || 'the reward'}”.
                          Consider raising the visits needed, or capping the stamps per bill below.
                        </p>
                      )}
                    </div>

                    {draft.earnBasis === 'item' && (
                      <div className="space-y-1.5">
                        <Label htmlFor="loyalty-earn-max" className="text-sm">Most stamps one bill can earn</Label>
                        <Input
                          id="loyalty-earn-max"
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={100}
                          step="1"
                          value={draft.earnMaxPerOrder}
                          onChange={(e) => setDraft({ ...draft, earnMaxPerOrder: e.target.value })}
                          placeholder="Optional — leave blank for no cap"
                        />
                        <p className="text-sm text-muted-foreground">
                          Stops a big group order from handing out a full card in one go. A bill with more
                          qualifying items than this still earns, just no more than the cap.
                        </p>
                      </div>
                    )}

                    {/* Which items earn. Empty set = everything earns, so the
                        summary line has to distinguish the two states clearly. */}
                    <div className="space-y-1.5">
                      <Label className="text-sm">Items that earn stamps</Label>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => setEligibleOpen(true)}>
                          Choose items
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          {eligibleCount === 0
                            ? 'Every item on the menu earns'
                            : `${eligibleCount} item${eligibleCount === 1 ? '' : 's'} selected`}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Limit earning to part of your menu — e.g. only your own coffee, not bought-in
                        desserts. A bill with none of the chosen items earns nothing.
                      </p>
                    </div>

                    {/* Earn-side integrity controls. A token purchase shouldn't
                        earn a visit, and one bill shouldn't be split to farm
                        several — but once earned, the reward spends freely (no
                        redeem-side limit by design). */}
                    <div className="space-y-1.5">
                      <Label htmlFor="loyalty-earn-min" className="text-sm">
                        {eligibleCount === 0
                          ? 'Minimum spend to earn a visit (PKR)'
                          : 'Minimum spend on qualifying items (PKR)'}
                      </Label>
                      <Input
                        id="loyalty-earn-min"
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        value={draft.earnMinSubtotal}
                        onChange={(e) => setDraft({ ...draft, earnMinSubtotal: e.target.value })}
                        placeholder="Optional — leave blank so any order earns a visit"
                      />
                      <p className="text-sm text-muted-foreground">
                        {eligibleCount === 0
                          ? 'How much a customer must spend for an order to count as a visit — stops a token purchase (e.g. just a water bottle) from earning a stamp.'
                          : 'Measured against the chosen items only, so a large bill with one token qualifying item still has to clear the bar.'}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <Label
                          htmlFor="loyalty-cooldown-toggle"
                          className={cn('text-sm', draft.earnBasis === 'item' && 'text-muted-foreground')}
                        >
                          Limit how often a customer can earn
                        </Label>
                        <Switch
                          id="loyalty-cooldown-toggle"
                          checked={draft.earnCooldownEnabled}
                          disabled={draft.earnBasis === 'item'}
                          onCheckedChange={(checked) => setDraft({ ...draft, earnCooldownEnabled: checked })}
                        />
                      </div>
                      {draft.earnCooldownEnabled && draft.earnBasis !== 'item' && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Earn at most once every</span>
                          <Input
                            id="loyalty-cooldown-hours"
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="0.5"
                            value={draft.earnCooldownHours}
                            onChange={(e) => setDraft({ ...draft, earnCooldownHours: e.target.value })}
                            className="w-24"
                            aria-label="Hours between earned visits"
                          />
                          <span className="text-sm text-muted-foreground">hours</span>
                        </div>
                      )}
                      <p className="text-sm text-muted-foreground">
                        {draft.earnBasis === 'item'
                          ? 'Not used when each item earns — a bill split into several orders earns the same either way, and a cooldown would wipe out a genuine second round. Switch back to “Each bill” to use it.'
                          : 'Stops one bill split across several orders from racking up multiple stamps. Set the gap you want between earned visits — e.g. 6 hours still lets a genuine lunch-and-dinner return earn twice; 24 makes it once a day. Leave off to earn on every qualifying order.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: live receipt preview — mirrors what prints for an
                enrolled customer mid-progression. The "reward ready"
                and "scan to save" variants have their own auto copy
                and aren't shown here. */}
            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                Receipt preview
              </Label>
              <LoyaltyReceiptPreview
                headline={previewHeadline}
                savedTo={PREVIEW_SAVED_TO}
                progress={previewProgress}
                threshold={previewThreshold}
                samplePunches={previewPunches}
              />
              <p className="text-sm text-muted-foreground">
                Shown on every receipt for enrolled customers.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 flex-wrap border-t border-border/60 pt-4">
            <Link
              to="/admin/marketing/posters"
              search={{ purpose: 'loyalty' }}
              className="inline-flex items-center gap-1.5 rounded-md border border-orange-300/70 bg-gradient-to-br from-amber-50 to-orange-50 px-3 py-2 text-sm font-medium text-orange-900 transition-colors hover:from-amber-100 hover:to-orange-100"
              title="Customize the loyalty poster — themes, fonts, colors, live preview."
            >
              <Sparkles className="h-4 w-4" />
              Open Poster Studio
            </Link>
            <Popover open={posterMenuOpen} onOpenChange={setPosterMenuOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="gap-1.5"
                  title="Print a counter poster, a foldable table tent, or a postcard-size counter card."
                >
                  <Download className="h-4 w-4" />
                  Quick print
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-1.5">
                <div className="px-2 py-1.5 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Choose a format
                </div>
                <PosterMenuItem
                  layout="a4"
                  icon={FileText}
                  label="Counter poster (A4)"
                  desc="Full-page hero — wall or counter."
                  onPick={openPoster}
                  pending={posterPendingLayout === 'a4'}
                />
                <PosterMenuItem
                  layout="tent"
                  icon={StickyNote}
                  label="Table tent (A5 folded)"
                  desc="Two-sided, foldable — sits on tables."
                  onPick={openPoster}
                  pending={posterPendingLayout === 'tent'}
                />
                <PosterMenuItem
                  layout="card"
                  icon={CreditCard}
                  label="Counter card (postcard)"
                  desc="Compact, fits an acrylic stand."
                  onPick={openPoster}
                  pending={posterPendingLayout === 'card'}
                />
                <div className="border-t border-border/60 px-3 py-2 text-sm leading-relaxed text-muted-foreground">
                  Opens in a new tab. Cmd+P to print or save as PDF.
                </div>
              </PopoverContent>
            </Popover>
            <Button
              type="button"
              onClick={handleSave}
              disabled={saveMutation.isPending || !dirty}
              className="gap-1.5"
            >
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
          </div>
          )}
        </div>

        <WalletCardsPanel>
          {/* Card appearance — how the customer's scanned screen (/q/loyalty,
              /q/wallet) and their Apple/Google Wallet pass look. Every field
              is an optional override; blank inherits the receipt branding so
              the zero-config default is unchanged. */}
          <div className="rounded-md border border-border/70">
            <button
              type="button"
              onClick={() => setAppearanceOpen((v) => !v)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-muted-foreground hover:bg-muted/50"
              aria-expanded={appearanceOpen}
            >
              {appearanceOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              <CreditCard className="h-3.5 w-3.5" />
              Card appearance
              <span className="ml-auto hidden text-xs font-normal text-muted-foreground sm:inline">
                Scan screen &amp; wallet pass
              </span>
            </button>
            {appearanceOpen && (
              <div className="grid grid-cols-1 gap-5 border-t border-border/70 p-3 md:grid-cols-[1.05fr_1fr]">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <div className="flex items-baseline justify-between">
                      <Label htmlFor="loyalty-screen-accent" className="text-sm">Accent colour</Label>
                      {draft.screenAccentColor.trim() !== '' && (
                        <button
                          type="button"
                          onClick={() => setDraft({ ...draft, screenAccentColor: '' })}
                          className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                        >
                          Reset to receipt
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={effAccent}
                        onChange={(e) => setDraft({ ...draft, screenAccentColor: e.target.value })}
                        className="h-9 w-12 shrink-0 cursor-pointer rounded border border-input bg-background p-1"
                        aria-label="Accent colour"
                      />
                      <Input
                        id="loyalty-screen-accent"
                        value={draft.screenAccentColor}
                        onChange={(e) => setDraft({ ...draft, screenAccentColor: e.target.value })}
                        placeholder={receiptBranding.accentColor || '#d97706'}
                        className="font-mono text-sm"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Tints the scan screen and the wallet pass. Blank = use your receipt accent.
                    </p>
                  </div>

                  {/* Page background of the public loyalty pages. The server
                      derives text, surfaces and borders from it, inverting the
                      neutrals when it's dark — so any colour stays readable. */}
                  <div className="space-y-1.5">
                    <div className="flex items-baseline justify-between">
                      <Label htmlFor="loyalty-screen-bg" className="text-sm">Page background</Label>
                      {draft.screenBackgroundColor.trim() !== '' && (
                        <button
                          type="button"
                          onClick={() => setDraft({ ...draft, screenBackgroundColor: '' })}
                          className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                        >
                          Reset to default
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={toSwatchHex(draft.screenBackgroundColor) || '#faf7f2'}
                        onChange={(e) => setDraft({ ...draft, screenBackgroundColor: e.target.value })}
                        className="h-9 w-12 shrink-0 cursor-pointer rounded border border-input bg-background p-1"
                        aria-label="Page background colour"
                      />
                      <Input
                        id="loyalty-screen-bg"
                        value={draft.screenBackgroundColor}
                        onChange={(e) => setDraft({ ...draft, screenBackgroundColor: e.target.value })}
                        placeholder="#faf7f2"
                        className="font-mono text-sm"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Background of the enrol + scan pages. Blank = the default warm cream.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm">Wallet card logo</Label>
                    <div className="flex items-center gap-3">
                      {effLogo ? (
                        <img
                          src={effLogo}
                          alt=""
                          className="h-10 w-10 rounded border border-border/60 bg-white object-contain"
                        />
                      ) : null}
                      <input
                        ref={screenLogoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0]
                          if (f) handleScreenLogoFile(f)
                          e.target.value = ''
                        }}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={() => screenLogoInputRef.current?.click()}>
                        Upload logo
                      </Button>
                      {draft.screenLogoUrl.trim() !== '' && (
                        <button
                          type="button"
                          onClick={() => setDraft({ ...draft, screenLogoUrl: '' })}
                          className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Square mark on the Apple / Google Wallet pass. Blank = use your receipt logo.
                    </p>
                  </div>

                  {/* The web pages get their own logo. The wallet mark is
                      square and cropped to fit a pass; a page can carry a
                      wide wordmark. Blank keeps the two in sync, which is
                      what every tenant had before this field existed. */}
                  <div className="space-y-1.5">
                    <Label className="text-sm">Web page logo</Label>
                    <div className="flex items-center gap-3">
                      {effPageLogo ? (
                        <img
                          src={effPageLogo}
                          alt=""
                          className="h-10 w-10 rounded border border-border/60 bg-white object-contain"
                        />
                      ) : null}
                      <input
                        ref={pageLogoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0]
                          if (f) handlePageLogoFile(f)
                          e.target.value = ''
                        }}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={() => pageLogoInputRef.current?.click()}>
                        Upload logo
                      </Button>
                      {draft.screenPageLogoUrl.trim() !== '' && (
                        <button
                          type="button"
                          onClick={() => setDraft({ ...draft, screenPageLogoUrl: '' })}
                          className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Shown on the join page and the scan / card page. Blank = use the wallet card logo.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm">Card banner</Label>
                    <div className="flex items-center gap-3">
                      {heroUrl ? (
                        <img
                          src={heroUrl}
                          alt=""
                          className="h-10 w-[78px] rounded border border-border/60 bg-white object-cover"
                        />
                      ) : null}
                      <input
                        ref={heroInputRef}
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0]
                          if (f) handleHeroFile(f)
                          e.target.value = ''
                        }}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={() => heroInputRef.current?.click()}>
                        Upload banner
                      </Button>
                      {heroUrl !== '' && (
                        <button
                          type="button"
                          onClick={() => setHeroDraft('')}
                          className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Wide photo behind the stamps, on the wallet card and Google card. PNG or JPG,
                      ideally 1032×336. Blank = your brand colour gradient.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="loyalty-screen-name" className="text-sm">Business name</Label>
                    <Input
                      id="loyalty-screen-name"
                      value={draft.screenBusinessName}
                      onChange={(e) => setDraft({ ...draft, screenBusinessName: e.target.value })}
                      maxLength={48}
                      placeholder={receiptBranding.businessName || 'Your business name'}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="loyalty-screen-headline" className="text-sm">Headline</Label>
                    <Input
                      id="loyalty-screen-headline"
                      value={draft.screenHeadline}
                      onChange={(e) => setDraft({ ...draft, screenHeadline: e.target.value })}
                      maxLength={48}
                      placeholder={effName}
                    />
                    <p className="text-sm text-muted-foreground">Big title on the scan screen. Blank = your business name.</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="loyalty-screen-subtext" className="text-sm">Subtext</Label>
                    <Input
                      id="loyalty-screen-subtext"
                      value={draft.screenSubtext}
                      onChange={(e) => setDraft({ ...draft, screenSubtext: e.target.value })}
                      maxLength={96}
                      placeholder="2 more stamps to your reward"
                    />
                    <p className="text-sm text-muted-foreground">Line under the progress. Blank = auto &ldquo;X more stamps to …&rdquo;.</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground">Customer preview</Label>
                  <LoyaltyScreenPreview
                    accent={effAccent}
                    logoUrl={effLogo || undefined}
                    pageLogoUrl={effPageLogo || undefined}
                    heroUrl={heroUrl || undefined}
                    headline={effHeadline}
                    subtext={draft.screenSubtext.trim()}
                    businessName={effName}
                    balance={previewPunches}
                    threshold={previewThreshold}
                    rewardLabel={previewRewardLabel}
                  />
                  <p className="text-sm text-muted-foreground">
                    What a customer sees after scanning the receipt QR, and how their saved wallet pass looks. The pass colour, logo, banner and name follow these settings.
                  </p>
                </div>
              </div>
            )}
          </div>
        </WalletCardsPanel>
      </div>

      {/* Saves immediately on its own (it writes a join table, not the config
          blob), so it doesn't participate in this page's Save button. */}
      <LoyaltyEligibleItemsDialog
        open={eligibleOpen}
        onOpenChange={setEligibleOpen}
        onSaved={() => eligibleQuery.refetch()}
      />

      <Dialog open={disableConfirmOpen} onOpenChange={setDisableConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Turn off the loyalty program?</DialogTitle>
            <DialogDescription className="leading-relaxed">
              The counter will stop adding punches and customers won't be able to earn or
              redeem rewards. Existing balances are kept and start working again the moment
              you turn it back on. You'll still need to <span className="font-medium text-foreground">Save</span> to apply this.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="ghost" onClick={() => setDisableConfirmOpen(false)}>
              Keep it on
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setDraft((d) => (d ? { ...d, isEnabled: false } : d))
                setDisableConfirmOpen(false)
              }}
            >
              Turn off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </MarketingShell>
  )
}

/** Live receipt preview — mirrors loyaltyReceiptSection's "saved"
 *  state in printCustomerReceipt.ts. Dashed outer box + dashed-rule
 *  inner frame, stars-flanked headline, a row of checkbox glyphs for
 *  the current punch count, and the substituted progress line. Visual
 *  match for ReceiptPreview on the Comeback coupon card. */
function LoyaltyReceiptPreview({
  headline,
  savedTo,
  progress,
  threshold,
  samplePunches,
}: {
  headline: string
  /** Sample "Awarded to <name> · ····1234" line — rendered above the dots
   *  to mirror the slip when a guest was captured at the counter. Empty
   *  → line skipped (matches the print path's graceful degradation). */
  savedTo?: string
  progress: string
  threshold: number
  samplePunches: number
}) {
  const safePunches = Math.max(0, Math.min(threshold, samplePunches))
  const dots = '☑'.repeat(safePunches) + '☐'.repeat(Math.max(0, threshold - safePunches))
  return (
    <div className="rounded-md border border-dashed border-foreground/30 bg-background px-4 py-5">
      <div className="border-y border-dashed border-foreground/60 px-2 py-4 text-center">
        <div className="text-sm font-extrabold uppercase tracking-wider text-foreground">
          <span>★</span>
          <span className="inline-block px-1.5">{headline}</span>
          <span>★</span>
        </div>
        {savedTo && (
          <div className="mt-2 text-sm font-semibold text-foreground/90">{savedTo}</div>
        )}
        <div className="mt-3 text-sm tracking-[0.18em] text-foreground">{dots}</div>
        <div className="mt-2 text-sm font-semibold leading-snug text-foreground">{progress}</div>
      </div>
    </div>
  )
}

/**
 * Wallet cards status panel — shows whether Apple/Google Wallet are
 * configured for this tenant (env-gated on the backend) and how many passes
 * are installed. Read-only: enabling a platform is a one-time ops step
 * (setting WALLET_* env vars), not a per-merchant toggle.
 */
/**
 * Google branding-sync status + manual push.
 *
 * Google builds an Android card from a "loyalty class" it creates once, the
 * first time any customer saves this restaurant's card — and then ignores the
 * class carried by every later save link. So editing the card appearance below
 * changes the scan screen and new Apple passes immediately, but an
 * already-saved Android card only restyles when the backend PATCHes that
 * class. Saving the settings does that automatically; this row reports whether
 * it worked and lets the owner retry.
 */
function GoogleBrandingSyncRow({ status }: { status?: GoogleClassSyncStatus }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const syncMutation = useMutation({
    mutationFn: () => apiClient.syncGoogleWalletClass(),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-wallet-stats'] })
      toast({
        title: 'Branding pushed',
        description: res.message || 'Saved cards will update shortly.',
      })
    },
    onError: (err: unknown) => {
      const description =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not reach Google. Try again shortly.'
      toast({ title: 'Push failed', description, variant: 'destructive' })
    },
  })

  const failed = Boolean(status?.last_error)
  const stale = Boolean(status && !status.in_sync)

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-border/70 bg-muted/30 px-3 py-2">
      <p className="min-w-0 flex-1 text-[12px] leading-snug text-muted-foreground">
        {failed ? (
          <span className="text-amber-700 dark:text-amber-300">
            Google rejected the last branding push. {status?.last_error}
          </span>
        ) : stale ? (
          <>
            Card appearance has changed since it was last sent to Google — cards already saved on
            customers’ phones still show the old look. Save your changes, or push now.
          </>
        ) : (
          <>
            Card branding is up to date on Google
            {status?.synced_at ? ` (${formatSyncedAt(status.synced_at)})` : ''}.
          </>
        )}
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0 gap-1.5"
        onClick={() => syncMutation.mutate()}
        disabled={syncMutation.isPending}
      >
        {syncMutation.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <RefreshCw className="h-3.5 w-3.5" />
        )}
        Push to Google
      </Button>
    </div>
  )
}

function formatSyncedAt(iso: string): string {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return 'just now'
  const mins = Math.floor((Date.now() - t) / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} h ago`
  return `${Math.floor(hours / 24)} d ago`
}

function WalletCardsPanel({ children }: { children?: ReactNode }) {
  const statsQuery = useQuery<APIResponse<WalletStats>>({
    queryKey: ['admin-wallet-stats'],
    queryFn: () => apiClient.getWalletStats(),
    staleTime: 30_000,
  })
  const s = statsQuery.data?.data
  const anyEnabled = Boolean(s?.apple_enabled || s?.google_enabled)

  return (
    <Card className="p-4 sm:p-5">
      <div className="mb-3 flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h4 className="text-base font-semibold text-foreground">Wallet cards</h4>
        <Badge variant={anyEnabled ? 'secondary' : 'outline'} className="ml-auto text-sm">
          {statsQuery.isLoading ? '…' : anyEnabled ? 'Active' : 'Not set up'}
        </Badge>
      </div>

      <p className="mb-4 text-sm text-muted-foreground">
        Let customers keep their stamp card in Apple&nbsp;Wallet or Google&nbsp;Wallet — it updates
        itself as they earn, and the counter can scan it to add a stamp. Cards appear on the receipt’s
        “Add to Wallet” button once a platform is configured.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <WalletPlatformRow
          name="Apple Wallet"
          enabled={Boolean(s?.apple_enabled)}
          installs={s?.apple_installs ?? 0}
          confirmed={s?.apple_confirmed}
          loading={statsQuery.isLoading}
        />
        <WalletPlatformRow
          name="Google Wallet"
          enabled={Boolean(s?.google_enabled)}
          invalid={Boolean(s?.google_configured_but_invalid)}
          installs={s?.google_installs ?? 0}
          confirmed={s?.google_confirmed}
          loading={statsQuery.isLoading}
        />
      </div>

      {s?.google_configured_but_invalid && !statsQuery.isLoading && (
        <p className="mt-3 text-[12px] leading-snug text-amber-700 dark:text-amber-300">
          Google Wallet credentials are set, but the service-account key
          (<code>WALLET_GOOGLE_SA_KEY</code>) couldn’t be parsed — so the card stays off. Check the
          backend boot logs for the parse error; common causes are surrounding quotes, a pasted full
          service-account JSON instead of just the key, or mangled newlines.
        </p>
      )}

      {s?.google_enabled && <GoogleBrandingSyncRow status={s.google_class} />}

      <WalletDeliveryRows delivery={s?.delivery} loading={statsQuery.isLoading} />

      {/* Card-appearance controls live here because they govern how the
          saved wallet pass (and the scan screen) look. */}
      {children && <div className="mt-4 border-t border-border/70 pt-4">{children}</div>}
    </Card>
  )
}

/**
 * Why the last push to each pass failed, if it did.
 *
 * Wallet delivery has failed silently over and over: a mistyped issuer id, a
 * 404 on the class artwork, a rejected class PATCH, Apple pushes sent with the
 * wrong credential. In every case the operator saw nothing. These rows put the
 * provider's own words on screen.
 *
 * `ok` and `never` are deliberately not rendered — the first is the happy path
 * (already covered by the confirmed count) and the second just means nobody has
 * earned a stamp since saving.
 */
function WalletDeliveryRows({
  delivery,
  loading,
}: {
  delivery?: PassHealth[]
  loading: boolean
}) {
  const problems = (delivery ?? []).filter((d) => d.status !== 'ok' && d.status !== 'never')
  if (loading || problems.length === 0) return null

  const label = (status: string): string => {
    switch (status) {
      case 'object_missing':
        return 'never finished adding the card'
      case 'no_devices':
        return 'no device registered to receive updates'
      case 'not_configured':
        return 'platform credentials missing on this backend'
      default:
        return 'update rejected'
    }
  }

  return (
    <div className="mt-3 space-y-1.5">
      {problems.map((d) => (
        <div
          key={`${d.platform}-${d.status}`}
          className="rounded-md border border-border/70 bg-muted/30 px-3 py-2 text-[12px] leading-snug"
        >
          <span className="font-medium capitalize text-foreground">{d.platform}</span>{' '}
          <span className="text-muted-foreground">
            · {d.count.toLocaleString()} {d.count === 1 ? 'card' : 'cards'} — {label(d.status)}
          </span>
          {d.last_error && (
            <div className="mt-0.5 break-words font-mono text-[11px] text-amber-700 dark:text-amber-300">
              {d.last_error}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function WalletPlatformRow({
  name,
  enabled,
  invalid = false,
  installs,
  confirmed,
  loading,
}: {
  name: string
  enabled: boolean
  invalid?: boolean
  installs: number
  /** Passes we've successfully pushed to — see WalletStats.google_confirmed. */
  confirmed?: number
  loading: boolean
}) {
  // "installed" counts rows written when the customer TAPPED Add to Wallet,
  // before the save completed. "confirmed" counts passes a push actually
  // reached. Showing only the first is what let a broken delivery path look
  // healthy for weeks.
  const unconfirmed = confirmed == null ? 0 : Math.max(0, installs - confirmed)
  return (
    <div className="flex items-center justify-between rounded-lg border border-border/70 px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{name}</div>
        <div className="text-[12px] text-muted-foreground tabular-nums">
          {loading
            ? '—'
            : confirmed == null
              ? `${installs.toLocaleString()} added`
              : `${installs.toLocaleString()} added · ${confirmed.toLocaleString()} confirmed`}
        </div>
        {!loading && unconfirmed > 0 && (
          <div className="text-[11px] leading-snug text-amber-700 dark:text-amber-300">
            {unconfirmed.toLocaleString()} tapped “Add” but no update has reached them yet
          </div>
        )}
      </div>
      <Badge
        variant={enabled ? 'secondary' : invalid ? 'destructive' : 'outline'}
        className={enabled ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200' : ''}
      >
        {loading ? '…' : enabled ? 'Live' : invalid ? 'Error' : 'Off'}
      </Badge>
    </div>
  )
}

function PosterMenuItem({
  layout,
  icon: Icon,
  label,
  desc,
  onPick,
  pending,
}: {
  layout: 'a4' | 'tent' | 'card'
  icon: typeof Download
  label: string
  desc: string
  onPick: (layout: 'a4' | 'tent' | 'card') => void
  pending: boolean
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(layout)}
      disabled={pending}
      className="flex w-full items-start gap-3 rounded-md px-2.5 py-2 text-left hover:bg-accent/60 focus:bg-accent/60 focus:outline-none disabled:opacity-60"
    >
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-foreground">{label}</span>
        <span className="block text-sm leading-snug text-muted-foreground">{desc}</span>
      </span>
    </button>
  )
}

// formatCurrency import-guard — referenced in JSX above via badge text.
export const _formatCurrencyBinding = formatCurrency
